import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { IconButton } from './IconButton';

export type InlineAlertVariant = 'info' | 'ok' | 'danger' | 'accent';

const ICONS: Record<InlineAlertVariant, IconName> = {
  info: 'info',
  ok: 'check',
  danger: 'alert',
  accent: 'alert',
};

/** DESIGN.md §9.1 InlineAlert. Status is never colour-alone (§2.4): every instance carries this
 * icon plus the message text. */
/**
 * MVP-357: `dismissLabel` was optional with an English `?? 'Dismiss'` fallback. Every enrolled
 * surface already passes it (`accessibleNames.test.ts` has pinned that since MVP-289), so the
 * fallback protected nothing and only kept an English string alive one layer below the gate. It
 * cannot be required outright — a state banner has no dismiss button to name — so it is paired with
 * `onDismiss` and the type system enforces the pair.
 */
export type InlineAlertProps = {
  variant: InlineAlertVariant;
  children: ReactNode;
} & (
  | {
      /** MVP-11 #13: transient failures are dismissable everywhere; omit for state banners.
       *
       * `| undefined` rather than a plain function because `GuestSearch`/`RequestSheet` forward an
       * optional prop straight through. That still satisfies what this pair is for: the label is
       * demanded from anyone who might render the button, and an alert that turns out not to have
       * one simply never reads it. */
      onDismiss: (() => void) | undefined;
      /** `ui/` stays dictionary-free (see `ui/SearchField.tsx`'s rule), so the caller passes
       * `t('common.dismiss')` rather than this file resolving it. */
      dismissLabel: string;
    }
  | { onDismiss?: undefined; dismissLabel?: undefined }
);

// `props` is read whole rather than destructured, for the same reason as `Dialog`'s: `dismissLabel`'s
// type is only known once `onDismiss` has been narrowed.
export function InlineAlert(props: InlineAlertProps) {
  const { variant, children } = props;
  return (
    <div className={`inline-alert inline-alert--${variant}`} role={variant === 'danger' ? 'alert' : 'status'}>
      <span className="inline-alert__icon">
        <Icon name={ICONS[variant]} size={16} />
      </span>
      <span className="inline-alert__text text-body">{children}</span>
      {props.onDismiss !== undefined && <IconButton icon="close" label={props.dismissLabel} size="sm" onClick={props.onDismiss} />}
    </div>
  );
}
